'use client';

/* ──────────────────────────────────────────────
 *  BookingTrustBar — discreet, persistent
 *  "secure payment" reassurance shown right under
 *  the progress bar once the cart holds something
 *  paid online. The full badge near the payment
 *  form (checkout) covers that step, so the bar
 *  hides there to avoid a duplicate.
 * ────────────────────────────────────────────── */

import { JSX } from 'react';
import { useBooking } from '../context/BookingContext';
import { PaymentReassurance } from './shared/PaymentReassurance';
import { cartRequiresOnlinePayment } from '../utils/payment-amounts';
import { isOutsideBookingPath } from '../utils/tunnel-steps';

export function BookingTrustBar(): JSX.Element | null {
  const { currentStep, ctx, params, labels } = useBooking();

  /* Hidden on transient states, outside the booking path (success, quote request: no payment there), and on
   * checkout (full badge already shown there). */
  if (
    currentStep === 'loading' ||
    currentStep === 'error' ||
    currentStep === 'checkout' ||
    isOutsideBookingPath(currentStep)
  ) {
    return null;
  }

  /* Hidden when the pro can't take online payments (no active Stripe payment method on the Connect
   * account). `allowed_payment_methods` is only populated when charges are enabled, so an empty list
   * means no payment happens in the tunnel — showing a "secure payment" badge would be misleading. */
  const methods = params.allowed_payment_methods;
  if (!methods || methods.length === 0) {
    return null;
  }

  /* Hidden until the cart holds a line paid online: an empty cart (logos before the visitor has even
   * seen the services) or a free-only cart (e.g. a free discovery call) has no payment to reassure. */
  if (!cartRequiresOnlinePayment(ctx.cartItems)) {
    return null;
  }

  return (
    <div className="bt-trust-bar">
      <PaymentReassurance
        layout="inline"
        methods={methods}
        secureLabel={labels.secure_payment}
        methodsLabel={labels.payment_methods_accepted}
      />
    </div>
  );
}
